import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import { cn } from "../../shared/ui";
import type { NotificationView, UnreadCount } from "../../shared/types/api";
import { NotificationRow } from "./NotificationRow";

/** How often to ask. Often enough to feel live, rarely enough to be free. */
const POLL_MS = 60_000;

/** What the panel shows before somebody goes to the full history. */
const PANEL_SIZE = 15;

/**
 * §89's in-app notifications.
 *
 * <p>Reads what this application actually sent — the same outbox rows the notification service is
 * handed — so the bell cannot show something different from the email that went with it.
 *
 * <p>Polled rather than pushed. A websocket for a badge that changes a few times a day is a
 * connection to keep alive, reconnect and reason about for no gain the reader would notice.
 *
 * <p>The panel is the recent end of a history that is kept in full, not the whole of it. Everything
 * it cannot fit stays on the notifications page rather than falling off the end, which is what
 * makes ignoring the bell safe: nothing is lost by not looking.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const unread = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => api.get<UnreadCount>("/api/v1/my/notifications/unread-count"),
    refetchInterval: POLL_MS,
  });

  const notifications = useQuery({
    queryKey: ["notifications", "list", PANEL_SIZE],
    queryFn: () =>
      api.get<NotificationView[]>(`/api/v1/my/notifications?page=0&size=${PANEL_SIZE}`),
    // Only fetched when the panel is opened; the badge alone does not need the list.
    enabled: open,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  const markAll = useMutation({
    mutationFn: () => api.post<void>("/api/v1/my/notifications/read-all"),
    onSuccess: refresh,
  });

  // Clicking outside closes the panel. Escape does too — a panel that traps the reader is worse
  // than one that closes when they did not quite mean it.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const count = unread.data?.unread ?? 0;

  const openNotification = async (notification: NotificationView) => {
    if (!notification.read) {
      await api.post(`/api/v1/my/notifications/${notification.id}/read`);
      refresh();
    }
    if (notification.link) {
      setOpen(false);
      navigate(notification.link);
    }
  };

  return (
    <div className="relative" ref={panel}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-label={count > 0 ? `Notifications, ${count} unread` : "Notifications"}
        aria-expanded={open}
        className="relative grid h-9 w-9 place-items-center rounded-lg text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
      >
        <Bell className="h-[18px] w-[18px]" />
        {count > 0 && (
          <span className="tnum absolute -right-0.5 -top-0.5 grid min-w-[18px] place-items-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold leading-[18px] text-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-40 w-[420px] overflow-hidden rounded-xl border border-ink-200 bg-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-ink-100 px-4 py-2.5">
            <span className="text-sm font-medium text-ink-900">Notifications</span>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="flex items-center gap-1 text-xs text-brand-600 hover:underline"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[520px] overflow-y-auto">
            {notifications.isPending ? (
              <p className="px-4 py-6 text-center text-sm text-ink-500">Loading…</p>
            ) : notifications.isError ? (
              <p className="px-4 py-6 text-center text-sm text-ink-600">
                Could not load your notifications.
              </p>
            ) : notifications.data.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-600">
                Nothing yet. You will hear about work assigned to you, mentions, blockers and
                anything waiting on your decision.
              </p>
            ) : (
              <ul className="divide-y divide-ink-100">
                {notifications.data.map((notification) => (
                  <li key={notification.id}>
                    <NotificationRow
                      notification={notification}
                      onOpen={(each) => void openNotification(each)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-ink-100 px-4 py-2.5 text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
              className={cn("text-xs text-brand-600 hover:underline")}
            >
              See all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
